// src/components/Settings/SettingsLayout.tsx
import React, { useState, useMemo, useLayoutEffect } from "react";
import { Box, Typography, IconButton, Avatar } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import Sidebar from "./Sidebar/Sidebar";
import SettingsViewArea from "./SettingsViewArea";
import AccountMenu from "../Menu/AccountMenu";
import { useAuth } from "../../hooks/useAuth";
import { authService } from "../../services/authService";

interface SettingsLayoutProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  themeMode: string;
  setThemeMode: (newMode: "light" | "dark" | "auto") => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
}

const SettingsLayout: React.FC<SettingsLayoutProps> = ({
  sidebarOpen,
  toggleSidebar,
  themeMode,
  setThemeMode,
  accentColor,
  setAccentColor,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const theme = useTheme();

  // Get user info from auth service
  const authData = authService.getAuthData();
  const userInfo = authData?.user_info;

  // Generate avatar initials from user's name
  const avatarInitials = useMemo(() => {
    if (!userInfo?.name) return "?";
    return userInfo.name
      .split(" ")
      .map((part: string) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [userInfo?.name]);

  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const handleAccountClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAccountAnchorEl(event.currentTarget);
  const handleAccountClose = () => setAccountAnchorEl(null);

  const handleBack = () => {
    navigate("/chat/new");
  };

  // Determine active tab based on URL
  const [activeTab, setActiveTab] = useState<string>("home");

  const handleNavigate = (path: string) => {
    if (path === "/settings") {
      setActiveTab("home");
    } else if (path === "/settings/theme") {
      setActiveTab("theme");
    } else if (path === "/settings/assistance") {
      setActiveTab("assistance");
    } else if (path === "/settings/users") {
      setActiveTab("users");
    } else if (path === "/settings/documents") {
      setActiveTab("documents");
    }
    navigate(path);
  };

  useLayoutEffect(() => {
    const path = location.pathname;
    if (path === "/settings") {
      setActiveTab("home");
    } else if (path === "/settings/theme") {
      setActiveTab("theme");
    } else if (path === "/settings/assistance") {
      setActiveTab("assistance");
    } else if (path === "/settings/users") {
      setActiveTab("users");
    } else if (path === "/settings/documents") {
      setActiveTab("documents");
    } else {
      setActiveTab("home");
      navigate("/settings");
    }
  }, [location.pathname]);

  return (
    <Box
      display="flex"
      height="100lvh"
      sx={{
        backgroundColor: "background.default",
        minHeight: "-webkit-fill-available",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: sidebarOpen ? "absolute" : "relative",
          width: sidebarOpen ? "240px" : "0",
          height: "100%",
          opacity: sidebarOpen ? 1 : 0,
          transition: "opacity 0.3s ease",
          zIndex: 10,
          backgroundColor: "background.paper",
          "@media (max-width: 600px)": {
            backgroundColor: `${theme.palette.background.paper}66`,
            backdropFilter: "blur(24px)",
            boxShadow: `4px 0px 10px ${
              theme.palette.mode === "dark"
                ? "rgba(0, 0, 0, 0.5)"
                : "rgba(0, 0, 0, 0.1)"
            }`,
          },
        }}
      >
        <Sidebar
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          handleBack={handleBack}
          handleNavigate={handleNavigate}
        />
      </Box>

      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        sx={{
          backgroundColor: "background.default",
          width: "100%",
          minWidth: 320,
          marginLeft: sidebarOpen ? "240px" : "0",
          transition: "margin-left 0.3s ease",
          "@media (max-width: 600px)": {
            marginLeft: "0",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          p={2}
        >
          <Box display="flex" gap={2} alignItems="center">
            {!sidebarOpen && (
              <IconButton onClick={toggleSidebar}>
                <MenuOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ fontWeight: 600, overflow: "hidden", WebkitLineClamp: 1 }}
            >
              จัดการระบบ
            </Typography>
          </Box>
          <Avatar
            component="button"
            variant="rounded"
            onClick={handleAccountClick}
            sx={{
              cursor: "pointer",
              marginLeft: "auto",
              fontSize: 16,
              width: 32,
              height: 32,
              bgcolor: "primary.main",
            }}
          >
            {avatarInitials}
          </Avatar>
        </Box>
        <SettingsViewArea
          activeTab={activeTab}
          themeMode={themeMode}
          setThemeMode={setThemeMode}
          accentColor={accentColor}
          setAccentColor={setAccentColor}
          handleNavigate={handleNavigate}
        />
      </Box>

      <AccountMenu
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={handleAccountClose}
        themeMode={themeMode}
        setThemeMode={setThemeMode}
        onLogout={logout}
      />
    </Box>
  );
};

export default SettingsLayout;
